import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { useDispatch } from "react-redux";
import { addCart } from "../redux/action";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { formatPKRFromUSD, formatDiscountedPKRFromUSD } from "../utils/currency";
import { getProducts } from "../utils/api";
import "./Products.css";

const Products = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState(data);
  const [loading, setLoading] = useState(false);
  const [activeCat, setActiveCat] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const mountedRef = useRef(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const addProduct = (product) => {
    dispatch(addCart(product));
  };

  const goToDetails = (id) => {
    navigate(`/product/${id}`);
  };

  useEffect(() => {
    mountedRef.current = true;
    const loadProducts = async () => {
      setLoading(true);
      const list = await getProducts();
      if (mountedRef.current) {
        setData(list);
        setFilter(list);
        setLoading(false);
        setActiveCat('all');
      }
    };

    loadProducts();
    // Initialize AOS once on mount
    AOS.init({ duration: 600, easing: 'ease-out', once: true, offset: 60 });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Parse and react to query changes (search/category/deals/new from navbar or URL)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || params.get('search') || '').trim();
    const catParamRaw = (params.get('cat') || '').toLowerCase();
    const dealsParam = params.get('deals');
    const newParam = params.get('new');
    setSearchTerm(q);

    const catMap = {
      men: "men's clothing",
      women: "women's clothing",
      electronics: "electronics",
      all: undefined
    };

    const token = ['men','women','electronics','all'].includes(catParamRaw) ? catParamRaw : null;
    const effectiveCat = token || activeCat; // URL 'cat' takes precedence when present
    if (token && token !== activeCat) {
      setActiveCat(token);
    }

    const filterByQ = (list) => {
      if (!q) return list;
      const lower = q.toLowerCase();
      const tokens = lower.split(/\s+/).filter(Boolean);
      return list.filter(p => {
        const text = (`${p.title} ${p.category} ${p.description || ''}`).toLowerCase();
        // Special handling: prefer explicit subcategory tags for handfree vs cable
        const isHandfreeQuery = tokens.includes('handfree');
        const isCableQuery = tokens.includes('cable') || tokens.includes('cables') || tokens.includes('data');

        // If user requested handfree, only return products explicitly marked as handfree or that mention 'handfree'
        if (isHandfreeQuery) {
          return (p.subcategory && p.subcategory === 'handfree') || text.includes('handfree');
        }

        // If user requested cable/data, prefer products explicitly tagged as cable and avoid items that are handfree
        if (isCableQuery) {
          if (p.subcategory && p.subcategory === 'cable') return true;
          // allow match on 'cable' text only if product is not a handfree (to avoid showing handfrees because their descriptions mention cables)
          if (text.includes('cable') && !text.includes('handfree')) return true;
          return false;
        }

        // Fallback: original token/synonym matching for other searches
        const augmented = [...tokens];
        // Keep 'airpods' strict: do NOT expand it to generic 'earbud' matches so Airpods section stays focused.
        // If the user searches for earbuds/earphone/headphone, expand with related non-AirPod synonyms.
        // Treat earbud/earphone/headphone as synonyms for each other (but do NOT include 'handfree')
        if (tokens.some(t => t.includes('earbud') || t.includes('earphone') || t.includes('headphone'))) augmented.push('earbud', 'earbuds', 'earphone', 'headphone');
        // match if any token (or synonym) appears in the product text
        return augmented.some(token => text.includes(token));
      });
    };

    const run = async () => {
      setLoading(true);
      // Load base list by category (either from URL or current active)
      let base;
      if (effectiveCat === 'all') {
        base = data.length ? data : await getProducts();
      } else {
        base = await getProducts(catMap[effectiveCat]);
      }
      // Apply search
      let out = filterByQ(base);
      // Apply deals/new filters if present in URL
      if (dealsParam) {
        out = out.filter(p => p.discountRate === 0.5);
      } else if (newParam) {
        out = out.filter(p => typeof p.id === 'string');
      }
      setFilter(out);
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, activeCat]);

  // Refresh AOS when filtered list changes
  useEffect(() => {
    try { AOS.refresh(); } catch {}
  }, [filter]);

  const Loading = () => {
    return (
      <>
        <div className="col-12 py-5 text-center">
          <Skeleton height={40} width={560} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
        <div className="col-md-4 col-sm-6 col-xs-8 col-12 mb-4">
          <Skeleton height={592} />
        </div>
      </>
    );
  };

  const filterProduct = async (cat) => {
    setLoading(true);
    // Clear search when user picks a category so buttons feel responsive
    if (searchTerm) {
      setSearchTerm('');
      // Clear query string in URL (keeps you on /product)
      navigate('/product');
    }
    const list = await getProducts(cat);
    setFilter(list);
    setLoading(false);
  };

  const ShowProducts = () => {
    return (
      <>
  <div className="buttons text-center py-3">
          <button
            className={`filter-btn m-2 ${activeCat === 'all' ? 'active' : ''}`}
            onClick={() => { 
              setActiveCat('all');
              if (searchTerm) { setSearchTerm(''); navigate('/product'); }
              setFilter(data);
            }}
          >
            All
          </button>
          <button
            className={`filter-btn m-2 ${activeCat === 'men' ? 'active' : ''}`}
            onClick={() => { filterProduct("men's clothing"); setActiveCat('men'); }}
          >
            Men's Clothing
          </button>
          <button
            className={`filter-btn m-2 ${activeCat === 'women' ? 'active' : ''}`}
            onClick={() => { filterProduct("women's clothing"); setActiveCat('women'); }}
          >
            Women's Clothing
          </button>
          {/* Removed Jewelery filter button */}
          <button
            className={`filter-btn m-2 ${activeCat === 'electronics' ? 'active' : ''}`}
            onClick={() => { filterProduct("electronics"); setActiveCat('electronics'); }}
          >
            Electronics
          </button>
        </div>

        {filter.map((product, idx) => {
          return (
            <motion.div
              id={product.id}
              key={product.id}
              className="col-lg-3 col-md-4 col-sm-6 col-6 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(idx * 0.03, 0.25) }}
            >
              <div
                className="card text-center h-100 shadow-sm border-0 product-card"
                key={product.id}
                onClick={() => goToDetails(product.id)}
                role="button"
                aria-label={`View details for ${product.title}`}
                data-aos="fade-up"
                data-aos-delay={(idx % 12) * 30}
              >
                {/* New badge (circular) */}
                {product.isNew && (
                  (() => {
                    const presets = ['New', 'Just In', 'New Arrival'];
                    const hash = String(product.id).split('').reduce((s,c)=>s + c.charCodeAt(0),0);
                    const label = product.newLabel || presets[Math.abs(hash) % presets.length];
                    return (
                      <div className="new-badge" title={label} aria-hidden="true">{label}</div>
                    );
                  })()
                )}
                <img
                  className="card-img-top p-2 product-img"
                  src={product.image}
                  alt={product.title}
                />
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="card-title text-start mb-1">
                      {product.title}
                    </h6>
                    {/* Show discount badge: 50% for local electronics, else 30% */}
                    <span className="badge bg-danger badge-glow">{product.discountRate===0.5 ? '50% OFF' : '30% OFF'}</span>
                  </div>
                </div>
                {!product.hidePrice && (
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted text-decoration-line-through small">
                          {formatPKRFromUSD(product.price)}
                        </span>
                        <span className="lead fw-bold">
                          {product.discountRate===0.5
                            ? formatPKRFromUSD(product.price * 0.5)
                            : formatDiscountedPKRFromUSD(product.price)}
                        </span>
                      </div>
                    </li>
                  </ul>
                )}
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2 mb-2 justify-content-center">
                    <span className="badge bg-success badge-glow">Free Delivery</span>
                    {product.dealType && (
                      <span className="badge bg-warning text-dark badge-glow">{product.dealType}</span>
                    )}
                  </div>
                  <Link
                    to={"/product/" + product.id}
                    className="btn btn-dark m-1 tw-transition tw-duration-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Buy Now
                  </Link>
                  <button
                    className="btn btn-dark m-1 tw-transition tw-duration-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.success("Added to cart");
                      addProduct(product);
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </>
    );
  };
  return (
    <>
      <div className="container my-3 py-3">
        {location.pathname === '/product' && (
          <div className="row">
            <div className="col-12">
              <h2 className="display-5 text-center">Latest Products</h2>
              <hr />
            </div>
          </div>
        )}
        <div className="products-section rounded-3 p-3">
          <div className="row justify-content-center">
            {loading ? <Loading /> : <ShowProducts />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;
